/**
 * Détection — étage 1 de l'import DNS.
 *
 * Avant de lire quoi que ce soit, trois questions se posent, et aucune n'a
 * de réponse évidente sur le corpus réel des DNS reçues :
 *
 *   1. **Quel format ?** Par signature binaire, jamais par extension —
 *      35 fichiers du corpus n'en ont aucune.
 *   2. **Quelle feuille ?** 204 classeurs en ont plusieurs, parfois 93.
 *   3. **Quelle ligne d'en-tête ?** Elle va de la ligne 1 à la ligne 41.
 *
 * Ce module reste **pur** : il ne lit aucun fichier et n'ouvre aucun
 * classeur. Il prend des octets déjà en mémoire et des cellules déjà
 * extraites, et rend une décision motivée. L'adaptateur qui transforme un
 * `.xlsx` en tableau de cellules viendra à part, avec sa dépendance — le
 * séparer permet de tester tout ce raisonnement sans fixture binaire.
 *
 * Chaque décision porte son **motif**. Une feuille écartée en silence est
 * une feuille qu'on ne pourra pas défendre six mois plus tard devant le
 * chargé de clientèle qui cherche ses trente salariés manquants.
 */
import { fusionnerEntete, normaliserLibelle, scoreEntete } from './synonymes.js';
/** Signatures binaires, en octets. */
const SIGNATURES = [
    // « PK\x03\x04 » — archive ZIP, donc .xlsx / .xlsm.
    { format: 'OOXML', octets: [0x50, 0x4b, 0x03, 0x04] },
    // Document composé OLE2 — Excel 97-2003 (.xls).
    { format: 'BIFF', octets: [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1] },
    // « %PDF »
    { format: 'PDF', octets: [0x25, 0x50, 0x44, 0x46] },
];
/**
 * Le format d'un fichier, **d'après son contenu et jamais son extension**.
 *
 * Sur le corpus, 35 fichiers n'ont pas d'extension du tout, et rien
 * n'interdit qu'un `.xls` soit en réalité un `.xlsx` renommé — c'est même
 * ce que fait un tableur quand on force le nom à l'enregistrement.
 *
 * `PDF` n'est pas une erreur : c'est un aiguillage vers la saisie de masse
 * ou l'extraction positionnelle, pas un rejet.
 */
export function formatDeFichier(octets) {
    for (const { format, octets: signature } of SIGNATURES) {
        if (signature.every((octet, i) => octets[i] === octet))
            return format;
    }
    return 'INCONNU';
}
/**
 * Une archive ZIP n'est pas forcément un classeur : un `.docx`, un `.ods` ou
 * un `.zip` portent la même signature. Un classeur Excel a une partie
 * `xl/workbook.xml`, dont le nom s'écrit en clair dans l'archive. Mesuré le
 * 16/10 : 6 fichiers des DNS finales passaient le dépôt puis échouaient à
 * l'écran des colonnes.
 */
export function estUnClasseurExcel(octets) {
    if (formatDeFichier(octets) !== 'OOXML')
        return false;
    const cherche = PARTIE_CLASSEUR;
    const premier = cherche[0] ?? 0;
    for (let i = octets.indexOf(premier); i >= 0; i = octets.indexOf(premier, i + 1)) {
        if (cherche.every((o, k) => octets[i + k] === o))
            return true;
    }
    return false;
}
const PARTIE_CLASSEUR = Array.from(new TextEncoder().encode('xl/workbook.xml'));
// ─────────────────────────────────────────────────────────────────────
//  2. Feuille utile
// ─────────────────────────────────────────────────────────────────────
/**
 * Feuilles dont le nom annonce autre chose qu'une liste de salariés.
 *
 * Elles ne sont **jamais écartées d'office** : leur nom les déclasse à
 * score égal, rien de plus. Une entreprise peut très bien avoir nommé
 * « TALON » la seule feuille qui contient son effectif, et un rejet sur le
 * nom perdrait tout le fichier.
 */
const NOMS_DECLASSES = [
    'EMBAUCHAGE',
    'DEBAUCHAGE',
    'EMBAUCHE',
    'EMBAUCHES',
    'DEBAUCHE',
    'DEBAUCHES',
    'TALON',
    'RECAP COTISATION',
    'RECAP',
    'JANVIER',
    'FEVRIER',
    'MARS',
    'AVRIL',
    'MAI',
    'JUIN',
    'JUILLET',
    'AOUT',
    'SEPTEMBRE',
    'OCTOBRE',
    'NOVEMBRE',
    'DECEMBRE',
];
/** Le format d'un scan, lu dans ses octets — jamais dans son extension ni son Content-Type. */
export function formatDeScan(octets) {
    const commence = (...signature) => signature.every((o, i) => octets[i] === o);
    if (commence(0x25, 0x50, 0x44, 0x46))
        return 'PDF';
    if (commence(0xff, 0xd8, 0xff))
        return 'JPEG';
    if (commence(0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a))
        return 'PNG';
    return null;
}
/**
 * Les marques HEIF d'une photo d'iPhone (HEVC). Pas `avif` : un AVIF n'est
 * pas ce qu'un téléphone de l'accueil produit, et il n'est pas accepté.
 */
const MARQUES_HEIF = ['heic', 'heix', 'heim', 'heis', 'hevc', 'hevx', 'mif1', 'msf1'];
/**
 * Une photo HEIC/HEIF, lue dans ses octets : une boîte `ftyp` en tête, puis
 * sa marque principale. Elle n'est jamais stockée telle quelle : le serveur
 * la convertit en JPEG (décision du TL du 23/10).
 */
export function estHeif(octets) {
    const texte = (debut) => String.fromCharCode(...octets.subarray(debut, debut + 4));
    return octets.length >= 12 && texte(4) === 'ftyp' && MARQUES_HEIF.includes(texte(8));
}
export function nomDeFeuilleDeclasse(nom) {
    const normalise = normaliserLibelle(nom);
    return NOMS_DECLASSES.some((motif) => normalise === motif || normalise.startsWith(`${motif} `));
}
/**
 * La feuille dont l'en-tête est le mieux reconnu.
 *
 * Une feuille vide passe derrière toute feuille qui porte des lignes, quel
 * que soit son score : un gabarit vierge peut avoir un meilleur en-tête que
 * la DNS remplie du même classeur, il n'en reste pas moins vide.
 *
 * À score égal, une feuille au nom déclassé passe derrière. À égalité
 * complète, la première l'emporte : l'ordre du classeur est celui que
 * l'entreprise a choisi, et il vaut mieux qu'un départage arbitraire.
 *
 * Aucune feuille n'est retenue si aucune n'obtient de score : mieux vaut
 * un fichier envoyé au mapping manuel qu'une feuille prise au hasard.
 */
export function choisirFeuille(feuilles, lignesExaminees = LIGNES_EXAMINEES_PAR_DEFAUT) {
    const examinees = feuilles.map((feuille) => {
        const entete = localiserEntete(feuille.lignes, lignesExaminees);
        const finEntete = entete.index === null ? null : entete.index + (entete.fusionAppliquee ? 1 : 0);
        return {
            nom: feuille.nom,
            score: entete.score,
            declassee: nomDeFeuilleDeclasse(feuille.nom),
            vide: finEntete === null || !feuille.lignes.slice(finEntete + 1).some(estUneLigneRemplie),
            indexEntete: entete.index,
        };
    });
    const candidates = examinees.filter((f) => f.score > 0);
    if (candidates.length === 0)
        return { retenue: null, ignorees: examinees };
    const meilleure = candidates.reduce((a, b) => (mieuxQue(b, a) ? b : a));
    return { retenue: meilleure, ignorees: examinees.filter((f) => f !== meilleure) };
}
function mieuxQue(candidate, tenante) {
    if (candidate.vide !== tenante.vide)
        return tenante.vide;
    if (candidate.score !== tenante.score)
        return candidate.score > tenante.score;
    return tenante.declassee && !candidate.declassee;
}
/** Une ligne de données : trois cellules remplies au moins — un « 1 » seul en colonne Nb n'en est pas une. */
export function estUneLigneRemplie(ligne) {
    return ligne.filter((c) => c.trim() !== '').length >= CELLULES_MIN_LIGNE_REMPLIE;
}
const CELLULES_MIN_LIGNE_REMPLIE = 3;
// ─────────────────────────────────────────────────────────────────────
//  3. Ligne d'en-tête
// ─────────────────────────────────────────────────────────────────────
/**
 * Jusqu'où chercher l'en-tête. Sur le corpus il descend jusqu'à la ligne
 * 41 — les fichiers commencent souvent par un logo, une adresse, un titre
 * et deux lignes vides. On prend une marge.
 */
export const LIGNES_EXAMINEES_PAR_DEFAUT = 50;
/**
 * La ligne d'en-tête d'une feuille.
 *
 * Chaque ligne candidate est évaluée deux fois : seule, puis fusionnée
 * avec la suivante. Le gabarit officiel écrit `DATE DE` sur une ligne et
 * `NAISSANCE` sur la suivante ; mais fusionner systématiquement casse les
 * fichiers à en-tête simple. On garde donc la meilleure des deux lectures,
 * mesurée — pas devinée.
 *
 * La fusion n'est tentée que si elle **améliore** le score. Une seconde
 * ligne qui est en réalité la première donnée ne peut donc jamais polluer
 * l'en-tête.
 */
export function localiserEntete(lignes, lignesExaminees = LIGNES_EXAMINEES_PAR_DEFAUT) {
    let meilleur = { index: null, libelles: [], score: 0, fusionAppliquee: false };
    const limite = Math.min(lignes.length, lignesExaminees);
    for (let i = 0; i < limite; i++) {
        const ligne = lignes[i] ?? [];
        const seule = scoreEntete(ligne);
        const suivante = lignes[i + 1];
        const fusionnee = suivante === undefined ? ligne : fusionnerEntete(ligne, suivante);
        const avecFusion = fusionnee === ligne ? seule : scoreEntete(fusionnee);
        // Une ligne qui ne dit rien (vide, titre) n'ouvre pas un en-tête : elle
        // prendrait la place de celui qui la suit.
        const prendLaFusion = seule > 0 && avecFusion > seule;
        const score = prendLaFusion ? avecFusion : seule;
        if (score > meilleur.score) {
            meilleur = {
                index: i,
                libelles: prendLaFusion ? fusionnee : ligne,
                score,
                fusionAppliquee: prendLaFusion,
            };
        }
    }
    return meilleur;
}
//# sourceMappingURL=detection.js.map